const DEFAULT_DB_NAME = "Leerling";
const DB_VERSION = 1;
const STORE_NAME = "QuizItems";
const METADATA_STORE = "Metadata";

// Function to check and initialize the database
const initializeDB = (dbName) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create QuizItems store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }

      // Create Metadata store if it doesn't exist
      if (!db.objectStoreNames.contains(METADATA_STORE)) {
        const metadataStore = db.createObjectStore(METADATA_STORE, {
          keyPath: "id",
        });
        metadataStore.put({ id: "metadata", lastUpdated: new Date() });
      }
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onerror = (event) => {
      console.error(`Database error with ${dbName}:`, event.target.error);
      reject(event.target.error);
    };
  });
};

// Function to handle database fallback mechanism
const openDatabase = async (dbName) => {
  try {
    const db = await initializeDB(dbName);
    if (db.objectStoreNames.contains(STORE_NAME)) {
      return db;
    } else {
      console.warn(
        `Object store ${STORE_NAME} not found in ${dbName}, falling back to ${DEFAULT_DB_NAME}`
      );
      return initializeDB(DEFAULT_DB_NAME);
    }
  } catch (error) {
    console.warn(
      `Failed to open ${dbName}, falling back to ${DEFAULT_DB_NAME}`
    );
    return initializeDB(DEFAULT_DB_NAME);
  }
};

// Function to check if the database is older than six months and should be deleted
const checkAndDeleteOldDatabases = () => {
  return new Promise((resolve, reject) => {
    const databasesRequest = indexedDB.databases();
    databasesRequest
      .then((databases) => {
        const fewMonthsAgo = new Date();
        fewMonthsAgo.setMonth(fewMonthsAgo.getMonth() - 11);

        databases.forEach((dbInfo) => {
          const request = indexedDB.open(dbInfo.name);
          request.onsuccess = (event) => {
            const db = event.target.result;

            if (db.objectStoreNames.contains(METADATA_STORE)) {
              const transaction = db.transaction(METADATA_STORE, "readonly");
              const store = transaction.objectStore(METADATA_STORE);
              const getRequest = store.get("metadata");

              getRequest.onsuccess = () => {
                const metadata = getRequest.result;
                if (metadata && new Date(metadata.lastUpdated) < fewMonthsAgo) {
                  console.info(
                    `Database ${dbInfo.name} is older than 11 months and will be deleted.`
                  );
                  indexedDB.deleteDatabase(dbInfo.name);
                }
              };
            } else {
              console.info(
                `No metadata found for ${dbInfo.name}, skipping deletion.`
              );
            }
          };
        });

        resolve();
      })
      .catch(reject);
  });
};

// Dynamically initialize or use existing DB based on the provided name
export const checkAndInitializeDB = async (dbName) => {
  await checkAndDeleteOldDatabases(); // Clean up old databases before initializing a new one
  const db = await openDatabase(dbName);

  // Ensure Metadata store exists before trying to update it
  if (db.objectStoreNames.contains(METADATA_STORE)) {
    const transaction = db.transaction(METADATA_STORE, "readwrite");
    const store = transaction.objectStore(METADATA_STORE);
    store.put({ id: "metadata", lastUpdated: new Date() });
  }
};

export const saveToDB = async (items, dbName) => {
  const db = await openDatabase(dbName);

  const transaction = db.transaction(STORE_NAME, "readwrite");
  const store = transaction.objectStore(STORE_NAME);

  items.forEach((item, index) => {
    store.put({
      id: item.id,
      answer: item.answer,
      attempts: item.attempts,
      order: index,
    });
  });

  transaction.oncomplete = () => {
    // console.info("All items saved to IndexedDB.");
  };

  transaction.onerror = (event) => {
    console.error("Transaction error:", event.target.error);
  };
};

export const getFromDB = (dbName) => {
  return new Promise(async (resolve, reject) => {
    const db = await openDatabase(dbName);

    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = () => {
      resolve(getAllRequest.result);
    };

    getAllRequest.onerror = (event) => {
      reject("Error retrieving data from IndexedDB:", event.target.error);
    };
  });
};

export const clearDB = async (dbName) => {
  const db = await openDatabase(dbName);

  const transaction = db.transaction(STORE_NAME, "readwrite");
  const store = transaction.objectStore(STORE_NAME);

  const clearRequest = store.clear();

  clearRequest.onsuccess = () => {
    console.info("All items cleared from IndexedDB.");
  };

  clearRequest.onerror = (event) => {
    console.error("Clear transaction error:", event.target.error);
  };
};

export const deleteFromDB = async (dbName, id) => {
  const db = await openDatabase(dbName);
  const transaction = db.transaction(STORE_NAME, 'readwrite');
  const store = transaction.objectStore(STORE_NAME);
  const deleteRequest = store.delete(id);

  return new Promise((resolve, reject) => {
    deleteRequest.onsuccess = () => {
      console.info(`Deleted item with ID ${id} from IndexedDB.`);
      resolve();
    };
    deleteRequest.onerror = (event) => {
      console.error('Error deleting item from IndexedDB:', event.target.error);
      reject(event.target.error);
    };
  });
}; // Import delete function
